/**
 * DisconnectedState — centred fallback shown instead of the workspace tabs when
 * Xero can't render. Three variants: not configured (env vars missing, no CTA),
 * configured-not-connected (Connect CTA), token-expired (amber Reconnect CTA).
 */
import { Loader2, Plug } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DisconnectedVariant } from '../lib/connection';

export function DisconnectedState({
  variant,
  isAdmin,
  onConnect,
  connecting,
}: {
  variant: DisconnectedVariant;
  isAdmin: boolean;
  onConnect: () => void;
  connecting: boolean;
}) {
  const expired = variant === 'token_expired';

  const heading =
    variant === 'not_configured'
      ? "Xero isn't configured yet"
      : expired
        ? 'Reconnect to Xero'
        : 'Connect Xero to get started';

  return (
    <div className="flex justify-center py-12">
      <Card className={cn('w-full max-w-md', expired && 'border-amber-300')}>
        <CardContent className="space-y-4 p-6 text-center">
          <div
            className={cn(
              'mx-auto flex h-12 w-12 items-center justify-center rounded-full',
              expired ? 'bg-amber-100' : 'bg-muted'
            )}
          >
            <Plug
              className={cn('h-6 w-6', expired ? 'text-amber-700' : 'text-muted-foreground')}
              aria-hidden="true"
            />
          </div>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold">{heading}</h2>

            {variant === 'not_configured' && (
              <p className="text-sm text-muted-foreground">
                Xero OAuth is not configured on the backend yet. Add <code>XERO_CLIENT_ID</code>,{' '}
                <code>XERO_CLIENT_SECRET</code>, and <code>XERO_REDIRECT_URI</code> to enable the
                connection.
              </p>
            )}

            {variant === 'not_connected' && (
              <p className="text-sm text-muted-foreground">
                Connecting lets the IMS push purchase orders, COGS journals, and stock-in-transit
                reclassifications to your Xero books. You'll need admin access in Xero to authorise
                the connection.
              </p>
            )}

            {expired && (
              <p className="text-sm text-muted-foreground">
                The Xero token has expired, so automatic pushes are paused. Reconnect to resume COGS
                and stock-in-transit journals.
              </p>
            )}
          </div>

          {variant === 'not_connected' && (
            <ul className="mx-auto max-w-xs space-y-1 text-left text-xs text-muted-foreground">
              <li className="flex gap-2">
                <span aria-hidden="true">•</span>
                Cost of Goods Sold journals (auto, daily 3 AM AEST)
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true">•</span>
                Stock-in-transit reclass journals (auto, on PO receive)
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true">•</span>
                Manual pushes from PO and SO screens
              </li>
            </ul>
          )}

          {variant !== 'not_configured' && isAdmin && (
            <div className="space-y-1.5">
              <Button
                className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                onClick={onConnect}
                disabled={connecting}
              >
                {connecting ? (
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : (
                  <Plug className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {expired ? 'Reconnect to Xero' : 'Connect to Xero'}
              </Button>
              <p className="text-[11px] text-muted-foreground">You'll be redirected to xero.com.</p>
            </div>
          )}

          {variant !== 'not_configured' && !isAdmin && (
            <p className="text-xs text-muted-foreground">
              Ask an admin to {expired ? 'reconnect' : 'connect'} Xero.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
